import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useCreateOrderExaminationMutation } from "Order/Modules/Examination/Api/Mutation/useCreateOrderExaminationMutation";
import {
	type CreateOrderExaminationFormModel,
	getCreateOrderExaminationFormSchema,
} from "Order/Modules/Examination/Components/Form/Schema/createExaminationFormSchema";
import { OrderPaths } from "Order/Routes/Routing";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2, Stack } from "@mui/material";
import dayjs from "dayjs";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

const formResource = Resources.base.examination.form;

type Props = {
	orderId: number;
	setIsExaminationModalOpen: (isOpen: boolean) => void;
};

export const CreateExaminationForm: FC<Props> = ({
	orderId,
	setIsExaminationModalOpen,
}) => {
	const navigate = useNavigate();
	const { t } = useResource();

	const formDefaultValues = {
		orderId: orderId,
		examinationDate: dayjs().format("YYYY-MM-DD"),
	};
	const form = useForm<CreateOrderExaminationFormModel>({
		resolver: zodResolver(getCreateOrderExaminationFormSchema(t)),
		defaultValues: formDefaultValues,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: createExamination, isPending } =
		useCreateOrderExaminationMutation(orderId);
	const { handleServerError } = useHandleServerError(form);

	const handleCreateExaminationSubmit = () => {
		handleServerError(createExamination, {
			onSuccess: () => {
				form.reset(formDefaultValues);
				setIsExaminationModalOpen(false);
				navigate(OrderPaths.Examination.path(orderId));
			},
		});
	};

	return (
		<>
			<Grid2 container spacing={1} size={{ xs: 12 }}>
				<Grid2 size={12}>
					<DatePicker
						name="examinationDate"
						label={t(formResource.examinationDate)}
						control={control}
						errors={errors}
					/>
				</Grid2>
			</Grid2>

			<Stack direction="row" gap={1} mt={2} justifyContent="flex-end">
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setIsExaminationModalOpen(false)}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={handleCreateExaminationSubmit}
					loading={isPending}
				>
					Save
				</Button>
			</Stack>
		</>
	);
};
